import { createContext, useContext, useState, useCallback, type ReactNode } from "react";

const STORAGE_KEY = "smartefi_module_favorites";

const DEFAULT_FAVORITES = [
  "dashboard", "dossiers", "exercices", "etats", "exports"
];

function readFavorites(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return DEFAULT_FAVORITES;
}

function persist(favs: string[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(favs));
}

interface ModuleFavoritesContextValue {
  favorites: string[];
  toggleFavorite: (key: string) => void;
  isFavorite: (key: string) => boolean;
  reorderFavorites: (fromKey: string, toKey: string) => void;
}

const ModuleFavoritesContext = createContext<ModuleFavoritesContextValue | null>(null);

export function ModuleFavoritesProvider({ children }: { children: ReactNode }) {
  const [favorites, setFavorites] = useState<string[]>(readFavorites);

  const toggleFavorite = useCallback((key: string) => {
    setFavorites(prev => {
      const next = prev.includes(key)
        ? prev.filter(k => k !== key)
        : [...prev, key];
      persist(next);
      return next;
    });
  }, []);

  const isFavorite = useCallback((key: string) => favorites.includes(key), [favorites]);

  const reorderFavorites = useCallback((fromKey: string, toKey: string) => {
    if (fromKey === toKey) return;
    setFavorites(prev => {
      const next = [...prev];
      const fromIdx = next.indexOf(fromKey);
      const toIdx = next.indexOf(toKey);
      if (fromIdx === -1 || toIdx === -1) return prev;
      next.splice(fromIdx, 1);
      next.splice(toIdx, 0, fromKey);
      persist(next);
      return next;
    });
  }, []);

  return (
    <ModuleFavoritesContext.Provider value={{ favorites, toggleFavorite, isFavorite, reorderFavorites }}>
      {children}
    </ModuleFavoritesContext.Provider>
  );
}

export function useModuleFavorites() {
  const ctx = useContext(ModuleFavoritesContext);
  if (!ctx) throw new Error("useModuleFavorites must be used within ModuleFavoritesProvider");
  return ctx;
}
